import { FC, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { Tr } from '@utils';
import { useService } from '@hooks';
import { dealerConfigService, simulationService, SimulationTask, SimulationTasksGroupedByCarListBand } from '@services';
import { Stack, Typography, TypographyProps } from '@mui/material';
import { DataTable, RouterLinkAnchor } from '@components/common';
import { PortalPageTitle } from '@components/portal/common';
import ArrowDropDownCircleIcon from '@mui/icons-material/ArrowDropDownCircle';

type TableDataType = SimulationTasksGroupedByCarListBand | SimulationTask;
const paddingX = 9;
const CellFont: FC<TypographyProps> = (props) => <Typography fontWeight={400} {...props} />;

export const ConversionList: FC = () => {
	const [expandedRows, setExpandedRows] = useState<TableDataType[]>([]);
	const { data: dealerConfig } = useService(() => dealerConfigService.find(), []);
	const { data = [] } = useService(() => simulationService.getLatestSimulationTasksGroupByCarListBand());

	const columns = useMemo<ColumnDef<TableDataType>[]>(
		() => [
			{
				accessorKey: 'conversionListTableName',
				header: () => <Tr.Portal path="conversion-list.car-band-list" />,
				enableSorting: false,
				minSize: 250,
				size: 250,
				cell: ({ row }) => {
					return <CellFont>{row.original.conversionListTableName}</CellFont>;
				},
			},
			{
				accessorKey: 'conversionListTableCurrentCar',
				header: () => <Tr.Portal path="conversion-list.current-cars" />,
				enableSorting: false,
				cell: ({ row }) => (
					<CellFont>
						{row.original.conversionListTableCurrentCar.length > 1 ? (
							<Tr.Portal path="conversion-list.various" />
						) : (
							row.original.conversionListTableCurrentCar[0]
						)}
					</CellFont>
				),
			},
			// {
			// 	accessorKey: 'conversionListTableVoltaageSuggests',
			// 	header: () => <Tr.Portal path="conversion-list.voltaage-suggests" />,
			// 	enableSorting: false,
			// 	size: 0.25,
			// 	maxsize: 0.25,
			// 	cell: ({ row }) => <CellFont>{row.original.conversionListTableVoltaageSuggests.join(', ')}</CellFont>
			// },
			{
				accessorKey: 'conversionListTableEvSelection',
				header: () => <Tr.Portal path="conversion-list.final-selection" />,
				enableSorting: false,
				cell: ({ row }) => {
					let link: string | undefined = undefined;
					const org = row.original as any;
					if (org instanceof SimulationTasksGroupedByCarListBand) {
						link = `./car-band-list-suggestions/${org.carListBand.id}`;
					} else if (org instanceof SimulationTask) {
						link = `./single-suggestion/${org.id}`;
					}
					return (
						<RouterLinkAnchor href={link} data-testid="conversion-list-ev-selection-link">
							<Typography>
								{row.original.conversionListTableEvSelection.length <= 0 && <Tr.Portal path="conversion-list.select" />}
								{row.original.conversionListTableEvSelection.length === 1 &&
									[
										row.original.conversionListTableEvSelection[0]?.brand,
										row.original.conversionListTableEvSelection[0]?.model,
									]
										.filter(Boolean)
										.join(' ')}
								{row.original.conversionListTableEvSelection.length > 1 && <Tr.Portal path="conversion-list.various" />}
							</Typography>
						</RouterLinkAnchor>
					);
				},
			},
			...((dealerConfig?.isVehiclePriceVisible
				? [
						{
							accessorKey: 'conversionListTableBudget',
							header: () => <Tr.Portal path="conversion-list.budget" />,
							enableSorting: false,
							cell: ({ row }) => <CellFont>{row.original.conversionListTableBudget}</CellFont>,
						},
						{
							accessorKey: 'conversionListTableLeasing',
							header: () => <Tr.Portal path="conversion-list.leasing" />,
							enableSorting: false,
							cell: () => (
								<RouterLinkAnchor>
									<Typography>
										<Tr.Portal path="conversion-list.select" />
									</Typography>
								</RouterLinkAnchor>
							),
						},
				  ]
				: []) as ColumnDef<TableDataType>[]),
		],
		[dealerConfig]
	);

	return (
		<Stack direction="column" spacing={3} sx={{ height: 1.0, maxHeight: 1.0, paddingY: 5, paddingX }}>
			<PortalPageTitle title={<Tr.Portal path="conversion-list.title" />} subtitle={<Tr.Portal path="conversion-list.subtitle" />} />
			<DataTable
				columns={columns}
				data={data}
				comparator={(d1, d2) => d1.id === d2.id}
				nestedDataKey="subRows"
				expandedRows={expandedRows}
				onExpandedRowsChange={setExpandedRows}
				expandIcon={({ ...props }) => (
					<ArrowDropDownCircleIcon {...props} sx={{ color: 'common.black' }} fontSize="small" color="inherit" />
				)}
				collapseIcon={({ ...props }) => (
					<ArrowDropDownCircleIcon
						{...props}
						sx={{ color: 'common.black', transform: 'rotate(180deg)' }}
						fontSize="small"
						color="inherit"
					/>
				)}
			/>
		</Stack>
	);
};
